import { AnimatePresence, motion } from 'framer-motion';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cx } from './cx';
import { isTypingTarget } from './hooks';
import { Icon, type IconName } from './Icon';

export type ToastTone = 'neutral' | 'success' | 'error';

export interface ToastInput {
  message: ReactNode;
  tone?: ToastTone;
  /** Adds a "Hoàn tác" button and binds Ctrl/Cmd+Z while the toast is visible. */
  undo?: () => void;
  action?: { label: string; onClick: () => void };
  /** Milliseconds; errors default to 6s, others to 4s. */
  duration?: number;
}

interface ToastItem extends ToastInput {
  id: number;
}

interface ToastApi {
  show: (toast: ToastInput) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const ICONS: Record<ToastTone, IconName | null> = {
  neutral: null,
  success: 'check',
  error: 'alert',
};

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast must be used inside <ToastProvider>');
  return api;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(1);

  const dismiss = useCallback((id: number) => {
    setItems((list) => list.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((toast: ToastInput) => {
    const id = next.current++;
    setItems((list) => [...list.slice(-2), { ...toast, id }]);
    return id;
  }, []);

  // Ctrl/Cmd+Z undoes the newest toast's action, unless the user is editing text.
  const latestUndo = [...items].reverse().find((t) => t.undo);
  useEffect(() => {
    if (!latestUndo) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'z' || !(e.metaKey || e.ctrlKey) || e.shiftKey) return;
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      latestUndo.undo?.();
      dismiss(latestUndo.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [latestUndo, dismiss]);

  const api = useMemo(() => ({ show, dismiss }), [show, dismiss]);

  // Top layer: a modal dialog or sheet would otherwise paint over the stack. Later top-layer
  // entries sit higher, so the stack is re-shown whenever a toast arrives or a dialog opens.
  const stack = useRef<HTMLElement>(null);
  const count = useRef(0);
  count.current = items.length;
  const raise = useCallback(() => {
    const el = stack.current;
    if (!el || typeof el.showPopover !== 'function') return;
    if (count.current === 0) return;
    if (el.matches(':popover-open')) el.hidePopover();
    el.showPopover();
  }, []);
  useEffect(() => {
    if (items.length > 0) return raise();
    // let the last toast finish its exit before leaving the top layer
    const t = setTimeout(() => {
      const el = stack.current;
      if (el?.matches(':popover-open')) el.hidePopover();
    }, 300);
    return () => clearTimeout(t);
  }, [items, raise]);
  useEffect(() => {
    const mo = new MutationObserver((records) => {
      if (records.some((r) => r.target instanceof HTMLDialogElement && r.target.open)) raise();
    });
    mo.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });
    return () => mo.disconnect();
  }, [raise]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <section
        ref={stack}
        popover="manual"
        aria-label="Thông báo"
        // bottom offset clears the editor's QuickBar, plus the bottom nav below md
        className="pointer-events-none fixed inset-x-0 bottom-[84px] top-auto max-md:bottom-[116px] m-0 h-auto w-auto flex-col items-center gap-2 overflow-visible border-0 bg-transparent p-0 px-4 [&:popover-open]:flex"
      >
        <AnimatePresence initial={false}>
          {items.map((t) => (
            <ToastView key={t.id} toast={t} onDone={() => dismiss(t.id)} />
          ))}
        </AnimatePresence>
      </section>
    </ToastContext.Provider>
  );
}

function ToastView({ toast, onDone }: { toast: ToastItem; onDone: () => void }) {
  const tone = toast.tone ?? 'neutral';
  const duration = toast.duration ?? (tone === 'error' ? 6000 : 4000);
  const [paused, setPaused] = useState(false);
  const left = useRef(duration);
  const startedAt = useRef(0);

  useEffect(() => {
    if (paused) return;
    startedAt.current = Date.now();
    const t = setTimeout(onDone, left.current);
    return () => {
      clearTimeout(t);
      left.current -= Date.now() - startedAt.current;
    };
  }, [paused, onDone]);

  const icon = ICONS[tone];
  return (
    <motion.div
      layout
      role={tone === 'error' ? 'alert' : 'status'}
      initial={{ opacity: 0, y: 16, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.96, transition: { duration: 0.15 } }}
      transition={{ type: 'spring', stiffness: 500, damping: 34 }}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      className="qb-root pointer-events-auto relative flex max-w-[min(520px,100%)] items-center gap-3 overflow-hidden rounded-xl bg-qb-ink py-2.5 pl-3.5 pr-2 font-qb text-[13px] text-white shadow-qb-pop"
    >
      {icon ? (
        <span
          className={cx(
            'flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
            tone === 'success' ? 'bg-qb-brand' : 'bg-qb-error',
          )}
        >
          <Icon name={icon} size={12} strokeWidth={2.6} />
        </span>
      ) : null}
      <span className="min-w-0 flex-1">{toast.message}</span>
      {toast.undo ? (
        <button
          type="button"
          onClick={() => {
            toast.undo?.();
            onDone();
          }}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 font-semibold text-[#7fd3a4] hover:bg-white/10"
        >
          <Icon name="undo" size={14} />
          Hoàn tác
          <span className="rounded border border-white/20 px-1 text-[10.5px] font-medium text-white/60 max-sm:hidden">
            Ctrl Z
          </span>
        </button>
      ) : null}
      {toast.action ? (
        <button
          type="button"
          onClick={() => {
            toast.action?.onClick();
            onDone();
          }}
          className="shrink-0 rounded-md px-2 py-1 font-semibold text-[#7fd3a4] hover:bg-white/10"
        >
          {toast.action.label}
        </button>
      ) : null}
      <button
        type="button"
        aria-label="Đóng thông báo"
        onClick={onDone}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white/60 hover:bg-white/10 hover:text-white"
      >
        <Icon name="close" size={14} />
      </button>
      <span
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-0.5 origin-left bg-white/30"
        style={{
          animation: `qb-drain ${duration}ms linear forwards`,
          animationPlayState: paused ? 'paused' : 'running',
        }}
      />
    </motion.div>
  );
}
